import { FC, ReactNode, useEffect, useRef } from "react";
import { isNull } from "lodash";
import { useTranslation } from "react-i18next"; // Import useTranslation

import useMainLayoutContainer from "./hooks/useMainLayoutContainer";

import { g_version as appVersion } from "version";

import { Loader } from "components/common/Loader";
import { MobileHeader } from "components/common/MobileHeader";
import { MobileMenu } from "components/common/MobileMenu";
import { Sidebar } from "components/common/Sidebar";
import { TasterMenuDrawer } from "components/common/TasterMenuDrawer";
import { ToastMessage } from "components/common/ToastMessage";
import { ConfirmationModal } from "components/common/modals/ConfirmationModal";

import * as UI from "styles/layouts/MainLayout";

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation(); // Initialize translation

  const {
    mobileMenuIsOpen,
    activeItem,
    tasterData,
    loading,
    submitting,
    fields,
    tasterMenuIsOpen,
    validFieldErrors,
    successToast,
    errorToast,
    logoutModalIsOpen,
    tasterDeleteModalIsOpen,
    handleMobileMenuOpen,
    handleMobileMenuClose,
    handleActiveItemChange,
    handleTasterMenuOpen,
    handleTasterMenuClose,
    handleTasterUpdate,
    handleToastClose,
    handleTasterAvailabilityUpdate,
    handleLogOutModalOpen,
    handleLogOutModalClose,
    onLogOut,
    handDeleteTasterModalOpen,
    handDeleteTasterModalClose,
    handleTasterDelete,
  } = useMainLayoutContainer();

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && contentRef && contentRef.current) {
      contentRef?.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  });

  return loading ? (
    <UI.MainLayout>
      <Loader fullPage withBlur whiteBlur />
    </UI.MainLayout>
  ) : (
    <UI.MainLayout>
      <Sidebar
        activeNavBtn={activeItem}
        setActiveNavBtn={handleActiveItemChange}
        user={{
          name: `${tasterData.firstName ?? ""} ${tasterData.lastName ?? ""}`,
        }}
        onUserClick={handleTasterMenuOpen}
        availability={
          isNull(tasterData.availableForExperiments)
            ? false
            : tasterData.availableForExperiments
        }
        setAvailability={handleTasterAvailabilityUpdate}
        onLogOut={handleLogOutModalOpen}
        version={appVersion}
      />

      <MobileHeader onClick={handleMobileMenuOpen} />

      <MobileMenu
        open={mobileMenuIsOpen}
        onClose={handleMobileMenuClose}
        activeNavBtn={activeItem}
        setActiveNavBtn={handleActiveItemChange}
        user={{
          name: `${tasterData.firstName ?? ""} ${tasterData.lastName ?? ""}`,
        }}
        onUserClick={handleTasterMenuOpen}
        availability={
          isNull(tasterData.availableForExperiments)
            ? false
            : tasterData.availableForExperiments
        }
        setAvailability={handleTasterAvailabilityUpdate}
        onLogOut={handleLogOutModalOpen}
        version={appVersion}
      />

      <UI.MainLayoutContent id={"main-layout-content"} ref={contentRef}>
        {children}
      </UI.MainLayoutContent>

      <TasterMenuDrawer
        open={!!(tasterMenuIsOpen && tasterData)}
        taster={tasterData}
        onClose={handleTasterMenuClose}
        fields={fields}
        loading={submitting}
        handleTasterUpdate={handleTasterUpdate}
        validFieldErrors={validFieldErrors}
        handleTasterRemove={handDeleteTasterModalOpen}
      />

      <ToastMessage
        severity="success"
        open={!!successToast}
        message={successToast as string}
        onClose={handleToastClose("success")}
      />

      <ToastMessage
        severity="error"
        open={!!errorToast}
        message={errorToast as string}
        onClose={handleToastClose("error")}
      />

      <ConfirmationModal
        type="info"
        open={logoutModalIsOpen}
        onClose={handleLogOutModalClose}
        onCancel={handleLogOutModalClose}
        onSubmit={onLogOut}
        title={t("log_out_confirmation_title")}
        text={t("log_out_confirmation_text")}
        submitBtnText={t("log_out_button")}
        cancelBtnText={t("stay_in_app_button")}
      />

      <ConfirmationModal
        type="warning"
        open={tasterDeleteModalIsOpen}
        onClose={handDeleteTasterModalClose}
        onCancel={handDeleteTasterModalClose}
        onSubmit={handleTasterDelete}
        title={t("delete_confirmation_title")}
        text={t("delete_confirmation_text")}
        submitBtnText={t("delete_confirmation_button")}
        cancelBtnText={t("stay_in_app_button")}
      />
    </UI.MainLayout>
  );
};

export default MainLayout;
